import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { TOTP_FIELDS } from '@/lib/auth/constants';
import { Button } from '@/lib/base/buttons/button';
import { HintText, useFormValidation } from '@/lib/base/forms';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { fetchWithDefaults } from '@/lib/http/client';
import { sanitizeError } from '@/lib/utils/errors';

import { useOrg } from '../../contexts/OrgContext';

interface TOTPData {
  secret: string;
  totp_url: string;
  qr_code_data_url?: string;
}

export default function TOTPSetup() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [totpData, setTotpData] = useState<TOTPData | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);

  const handleCodeChange = useCallback(
    (value: string) => setCode(value.replace(/\D/g, '').slice(0, 6)),
    []
  );

  const handleCopyCodes = useCallback(async () => {
    const codesText = recoveryCodes.join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      toast.success('Recovery codes copied to clipboard');
    } catch {
      toast.error("We couldn't copy your recovery codes. Try again.");
    }
  }, [recoveryCodes]);

  const handleContinueAfterCodes = useCallback(() => {
    navigate(`/${currentOrg?.id}/profile`, {
      state: {
        message: 'Two-factor authentication has been enabled successfully.',
      },
    });
  }, [navigate, currentOrg?.id]);

  const handleCancel = useCallback(
    () => navigate(`/${currentOrg?.id}/profile`),
    [navigate, currentOrg?.id]
  );

  const { getFieldProps, touchAll, isValid } = useFormValidation({
    fields: TOTP_FIELDS,
    values: { code },
  });
  const codeField = getFieldProps('code');

  useEffect(() => {
    loadTOTPData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: load TOTP data once
  }, []);

  const loadTOTPData = async () => {
    try {
      setLoading(true);

      const response = await fetchWithDefaults('/users/api/totp-setup/');

      if (response.ok) {
        const data = await response.json();
        setTotpData(data);
      } else if (response.status === 409) {
        // Already configured, redirect to management
        navigate('/account/authenticators');
        return;
      } else {
        setError(
          "We couldn't load your two-factor authentication setup. Try again."
        );
      }
    } catch (err) {
      console.error('Error loading TOTP data:', err);
      setError(
        "We couldn't load your two-factor authentication setup. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      touchAll();
      if (!isValid) {
        return;
      }

      try {
        setSubmitting(true);

        const response = await fetchWithDefaults('/users/api/totp-activate/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code: code.trim() }),
        });

        if (response.ok) {
          const result = await response.json();
          if (result.recovery_codes) {
            setRecoveryCodes(result.recovery_codes);
            setShowRecoveryCodes(true);
          } else {
            navigate(`/${currentOrg?.id}/profile`, {
              state: {
                message:
                  'Two-factor authentication has been enabled successfully.',
              },
            });
          }
        } else {
          const errorData = await response.json();
          setError(
            errorData.error
              ? sanitizeError(errorData.error)
              : 'Invalid verification code'
          );
        }
      } catch (err) {
        console.error('Error activating TOTP:', err);
        setError("We couldn't activate two-factor authentication. Try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [isValid, touchAll, code, navigate, currentOrg?.id]
  );

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-full'>
        <LoadingIndicator size='lg' />
      </div>
    );
  }

  if (showRecoveryCodes && recoveryCodes.length > 0) {
    return (
      <div className='min-h-full bg-bg-primary flex items-center justify-center p-4'>
        <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm w-full max-w-md'>
          <div className='px-6 py-5 border-b border-border-secondary'>
            <h2 className='text-lg font-semibold text-secondary text-center'>
              Save your recovery codes
            </h2>
          </div>

          <div className='p-6 space-y-4'>
            <AlertFloating
              color='warning'
              title='Important'
              description='Save these codes in a safe place. You can use them to access your account if you lose your authenticator device. Each code can only be used once.'
            />

            <div className='bg-bg-secondary p-4 rounded-lg'>
              <div className='grid grid-cols-2 gap-2'>
                {recoveryCodes.map(code => (
                  <div
                    key={code}
                    className='bg-bg-primary p-2 rounded text-center font-mono text-sm text-primary'
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className='flex gap-2'>
              <Button
                onClick={handleCopyCodes}
                color='secondary'
                size='md'
                className='flex-1'
              >
                Copy codes
              </Button>
              <Button
                onClick={handleContinueAfterCodes}
                color='primary'
                size='md'
                className='flex-1'
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!totpData) {
    return (
      <div className='min-h-full bg-bg-primary flex items-center justify-center p-4'>
        <AlertFloating
          color='error'
          title={
            error ||
            "We couldn't load your two-factor authentication setup. Try again."
          }
          description='Please try again or contact support.'
        />
      </div>
    );
  }

  return (
    <div className='min-h-full bg-bg-primary flex items-center justify-center p-4'>
      <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm w-full max-w-md'>
        <div className='px-6 py-5 border-b border-border-secondary'>
          <h2 className='text-lg font-semibold text-secondary text-center'>
            Enable two-factor authentication
          </h2>
        </div>

        <div className='p-6'>
          <div className='space-y-4'>
            <div className='text-center'>
              <p className='text-sm mb-4 text-primary'>
                Scan this QR code with your authenticator app (like Google
                Authenticator, Authy, or 1Password):
              </p>

              {totpData.qr_code_data_url && (
                <div className='flex justify-center mb-4'>
                  <img
                    src={totpData.qr_code_data_url}
                    alt='QR Code for TOTP setup'
                    className='border border-border-secondary rounded-lg'
                  />
                </div>
              )}

              <div className='text-center mb-4'>
                <p className='text-xs text-tertiary'>
                  Or use this URL: <br />
                  <code className='text-xs break-all'>{totpData.totp_url}</code>
                </p>
              </div>

              <div className='border-t border-border-secondary my-4' />

              <div className='text-center'>
                <p className='text-sm mb-2 text-primary'>
                  Manually enter this secret key:
                </p>
                <div className='bg-bg-secondary p-3 rounded-lg'>
                  <code className='text-sm font-mono text-primary'>
                    {totpData.secret}
                  </code>
                </div>
              </div>
            </div>

            <form onSubmit={handleActivate} noValidate className='space-y-4'>
              <Input
                label='Enter the 6-digit code from your app:'
                type='text'
                value={code}
                onChange={handleCodeChange}
                onBlur={codeField.onBlur}
                isInvalid={codeField.showError}
                showError={codeField.showError}
                inputClassName='text-center text-lg'
                placeholder='123456'
                maxLength={6}
                isRequired
                size='md'
              />

              {codeField.showError && codeField.error && (
                <HintText isInvalid showError className='mt-1.5'>
                  {codeField.error}
                </HintText>
              )}

              {error && (
                <AlertFloating color='error' title={error} description='' />
              )}

              <div className='flex gap-2'>
                <Button
                  type='button'
                  onClick={handleCancel}
                  color='tertiary'
                  size='md'
                  isDisabled={submitting}
                  className='flex-1'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  isDisabled={submitting}
                  color='primary'
                  size='md'
                  isLoading={submitting}
                  className='flex-1'
                >
                  Activate
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
